<template>
  <div class="base-cropper">
    <div v-if="imageSrc" class="base-cropper__wrapper">
      <AdvancedCropper
        ref="cropperRef"
        class="base-cropper__cropper"
        :src="imageSrc"
        default-boundaries="fill"
        image-restriction="fit-area"
        :stencil-props="{
          aspectRatio: props.aspectRatio,
          movable: true,
          resizable: true,
        }"
        @ready="handleReady"
      />
    </div>

    <p v-else class="base-cropper__placeholder">
      Нет изображения
    </p>
  </div>
</template>

<script setup lang="ts">
import { Cropper as AdvancedCropper } from 'vue-advanced-cropper'

import { getDataUrlFromFirstFile, getExtensionFromDataURL, getMimeTypeFromDataURL, isDataURL } from '../../lib/file'

interface Props {
  file?: File
  src?: string
  height?: string
  aspectRatio?: number
}

const props = withDefaults(defineProps<Props>(), {
  height: '400px',
})
const emit = defineEmits<{
  (e: 'croppedFile', value: File): void
}>()

const croppedImage = defineModel<string | null>()

const cropperRef = ref<InstanceType<typeof AdvancedCropper> | null>(null)
const imageSrc = ref<string>('')

watch(
  () => props.file,
  (newValue) => {
    if (!newValue) {
      return
    }

    imageSrc.value = getDataUrlFromFirstFile([newValue])
  },
  { immediate: true },
)

watch(
  () => props.src,
  (newValue) => {
    if (!newValue) {
      return
    }

    imageSrc.value = newValue
  },
  { immediate: true },
)

function handleReady() {
  nextTick(() => {
    cropperRef.value?.refresh()
  })
}

async function cropImage() {
  return new Promise((resolve) => {
    if (!cropperRef.value) {
      resolve(null)
      return
    }

    const result = cropperRef.value.getResult()
    if (!result || !result.canvas) {
      console.warn('Не удалось получить обрезанное изображение.')
      resolve(null)
      return
    }

    croppedImage.value = result.canvas.toDataURL('image/png')

    result.canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null)
        return
      }

      let fileName = 'cropped-image.png'
      let fileType = 'image/png'

      if (props.file) {
        fileName = props.file.name
        fileType = props.file.type
      }
      else if (props.src && isDataURL(props.src)) {
        const mimeMatch = getMimeTypeFromDataURL(props.src)
        if (mimeMatch) {
          fileType = mimeMatch
          fileName = `cropped-image.${getExtensionFromDataURL(props.src)}`
        }
      }

      const file = new File([blob], fileName, { type: fileType })
      emit('croppedFile', file)

      resolve(file)
    })
  })
}

function resetCropper() {
  croppedImage.value = null
}

defineExpose({
  cropImage,
  resetCropper,
})
</script>

<style lang="scss" scoped>
.base-cropper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;

  &__wrapper {
    position: relative;
    width: 100%;
    max-width: 100%;
    height: 100%;
  }

  &__cropper {
    width: 100%;
    height: v-bind(height);
    border-radius: var(--radius-sm);
    overflow: hidden;

    :deep(.vue-advanced-cropper) {
      height: 100%;
    }
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 10px;
  }

  &__placeholder {
    color: var(--color-foreground-caption);
    font-size: 16px;
    font-weight: bold;
  }
}
</style>
